import React, { Component, ReactNode } from 'react';
import Dropzone, { DropzoneState } from 'react-dropzone';
import { Icon } from '@trlogic/ui-pack';
import { Dialog } from 'primereact/dialog';
import FileSizeErrorComponent from './FileSizeErrorComponent';
import LanguageUtil from '../../util/LanguageUtil';

class FileUploader extends Component<Props, State> {
  public state: State = new State();

  public render(): ReactNode {
    const { file, progress, maxBytes, onChangeFile, percent, disabled } = this.props;
    const { errorVisible } = this.state;

    return (
      <div className='flex flex-col w-full h-full py-5 px-2'>
        {file ? (
          <div className={'flex flex-col h-48 overflow-y-scroll'}>
            <div className={'flex  items-center justify-between w-[300px] gap-y-4 px-2 py-2 border'}>
              <Icon icon={'csv-file'} size={45} />
              <div className={'flex flex-col gap-y-2 text-2xs text-primary'}>
                <p>{file.name}</p>
                <p>{(file.size / (1024 * 1024)).toFixed(4)} mb</p>
                {progress && (
                  <div className='h-2 w-full bg-gray-200 rounded'>
                    <div className='h-full bg-green-500 rounded' style={{ width: `${percent}%` }}></div>
                  </div>
                )}
              </div>
              <div onClick={() => onChangeFile(null)}>
                <Icon icon={'close'} className={'cursor-pointer'} size={30} />
              </div>
            </div>
          </div>
        ) : (
          <Dropzone disabled={disabled} maxFiles={1} onDrop={this.onDrop}>
            {({ getRootProps, getInputProps }: DropzoneState) => (
              <div {...getRootProps()} className={'flex flex-col gap-y-2 items-center justify-center w-full h-full text-md'}>
                {
                  <div className={'flex flex-col gap-y-2 items-center justify-center w-full h-full'}>
                    <Icon icon={'no-folder'} size={40} />
                    <p>{LanguageUtil.get({ id: 'common.label.clickOrDrag' })}</p>
                    <p>{LanguageUtil.get({ id: 'common.label.filesAreAccept' }, { 0: `${this.getText()}` })}</p>
                    {this.getText() == 'CSV' && <p>{LanguageUtil.get({ id: 'common.label.csvFiles' })}</p>}
                    <p>{LanguageUtil.get({ id: 'common.label.youCanSelectFile' })}</p>
                    <p className={'underline text-primary font-bold cursor-pointer'}>{LanguageUtil.get({ id: 'common.label.clickingHere' })}</p>
                  </div>
                }
                <input {...getInputProps()} />
              </div>
            )}
          </Dropzone>
        )}
        <Dialog
          style={{ borderRadius: 24 }}
          contentStyle={{ borderRadius: 24 }}
          headerStyle={{ borderTopRightRadius: 24, borderTopLeftRadius: 24 }}
          onHide={this.onCloseError}
          visible={errorVisible}
          dismissableMask
          closable
          closeOnEscape
        >
          <FileSizeErrorComponent onClose={this.onCloseError} onOk={this.onOkFileError} maxSizeInMBytes={Number(((maxBytes ?? 0) / 1048576).toFixed(2))} />
        </Dialog>
      </div>
    );
  }

  private onDrop = (uploadedFiles: File[]) => {
    const { maxBytes, acceptedFiles, onChangeFile } = this.props;
    const file = uploadedFiles[0];
    const isAccepted = acceptedFiles.includes(file.type);
    if (!isAccepted) {
      onChangeFile(null);
      return alert(`Only ${this.getText()} files are accepted`);
    }
    if (maxBytes != undefined && file.size > maxBytes) {
      this.setState({ errorVisible: true, tempFile: file, uploadError: 'ASK_SIZE' });
      return;
    }
    onChangeFile(file);
  };

  private onOkFileError = () => {
    const { tempFile } = this.state;
    const { onChangeFile } = this.props;
    onChangeFile(tempFile);
    this.onCloseError();
  };

  private onCloseError = () => {
    this.setState({ errorVisible: false, uploadError: null, tempFile: null });
  };

  private getText = () => {
    const { acceptedFiles } = this.props;
    switch (acceptedFiles[0]) {
      case 'application/json':
      case 'text/json':
        return 'JSON';
      case 'text/csv':
        return 'CSV';
      default:
        return '';
    }
  };
}

interface Props {
  acceptedFiles: string[];
  disabled?: boolean;
  onChangeFile: (file: File | null) => void;
  file: File | null;
  percent: number;
  progress: UploadProgress;
  maxBytes?: number;
}

class State {
  errorVisible: boolean = false;
  uploadError: UploadError | null = null;
  tempFile: File | null = null;
}

export type UploadError = 'ASK_SIZE' | 'TYPE';
export type UploadProgress = 'idle' | 'uploading' | 'uploaded' | 'error';

export default FileUploader;
